import { motion } from "framer-motion";
import { CalendarDays, Users, Car, Loader2, X, Ticket } from "lucide-react";

const DAYS = [
  { id: "day1", en: "Day 1", ar: "اليوم الأول", date: "Thu · Dec 10" },
  { id: "day2", en: "Day 2", ar: "اليوم الثاني", date: "Fri · Dec 11" },
  { id: "day3", en: "Day 3", ar: "اليوم الثالث", date: "Sat · Dec 12" },
];

const VISITOR_DAYS = [
  { id: "dec10", en: "Dec 10", ar: "١٠ ديسمبر" },
  { id: "dec11", en: "Dec 11", ar: "١١ ديسمبر" },
  { id: "dec12", en: "Dec 12", ar: "١٢ ديسمبر" },
];

const ASSIGNMENT_LABELS = {
  day1: "Day 1", day2: "Day 2", day3: "Day 3",
  day1_day3: "Day 1 & Day 3", indoor: "Indoor 3-day", outdoor: "Outdoor 3-day",
};

const Ar = ({ children }) => (
  <span dir="rtl" lang="ar">{children}</span>
);

const Num = ({ value, loading, testid }) => (
  <span className="font-heading font-extrabold text-3xl text-white" data-testid={testid}>
    {value === null || value === undefined ? (loading ? "…" : "—") : value}
  </span>
);

function DayCard({ day, data, active, loading, onClick }) {
  const breakdown = data?.by_assignment || {};
  return (
    <motion.button
      type="button"
      whileHover={{ y: -2 }}
      onClick={onClick}
      aria-pressed={active}
      data-testid={`attendance-day-card-${day.id}`}
      className={`text-left border p-4 transition-colors ${
        active ? "border-[#F5C518] bg-[#F5C518]/10" : "border-white/10 bg-white/[0.02] hover:border-white/30"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="label-kicker" style={{ color: active ? "#F5C518" : "#FFFFFF" }}>
          {day.en} · <Ar>{day.ar}</Ar>
        </div>
        <CalendarDays className="w-4 h-4 text-[#F5C518]" />
      </div>
      <Num value={data?.total} loading={loading} testid={`attendance-day-total-${day.id}`} />
      <div className="text-[10px] text-white/40 tracking-wider uppercase mt-1">{day.date}</div>
      <div className="text-[11px] text-white/50 mt-2 leading-relaxed">
        {Object.entries(breakdown).map(([k, v]) => `${ASSIGNMENT_LABELS[k]} ${v}`).join(" · ")}
      </div>
      <div className="text-[10px] text-white/40 mt-2 uppercase tracking-wider">
        {active ? "Showing in list · tap to clear" : "Tap to show in list"}
      </div>
    </motion.button>
  );
}

function TotalCard({ icon: Icon, en, ar, value, loading, testid, children }) {
  return (
    <div className="border border-white/10 bg-white/[0.02] p-4" data-testid={`${testid}-card`}>
      <div className="flex items-center justify-between mb-2">
        <div className="label-kicker text-white">{en} · <Ar>{ar}</Ar></div>
        <Icon className="w-4 h-4 text-[#F5C518]" />
      </div>
      <Num value={value} loading={loading} testid={testid} />
      <div className="text-[11px] text-white/50 mt-2 leading-relaxed">{children}</div>
    </div>
  );
}

export const AttendanceSummary = ({ summary, loading, error, attendDay, onSelectDay }) => {
  const p = summary?.participants || {};
  const v = summary?.vehicles || {};
  const days = summary?.days || {};
  const vis = summary?.visitors || {};
  return (
    <section className="mb-8" data-testid="attendance-summary">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3">
        <h3 className="font-heading font-bold text-sm tracking-[0.2em] uppercase text-[#F5C518]">
          Attendance &amp; Vehicles · <Ar>الحضور والمركبات</Ar>
        </h3>
        <span className="text-[10px] text-white/40 tracking-wider uppercase">
          Approved only (incl. checked-in) · pending / rejected / archived excluded
        </span>
        {loading && <Loader2 className="w-3.5 h-3.5 text-[#F5C518] animate-spin" data-testid="attendance-summary-loading" />}
      </div>
      {error && (
        <div className="mb-3 px-3 py-2 border border-orange-500/60 bg-orange-500/10 text-orange-200 text-xs" role="alert" data-testid="attendance-summary-error">
          Attendance summary unavailable: {error}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        {DAYS.map((d) => (
          <DayCard
            key={d.id}
            day={d}
            data={days[d.id]}
            loading={loading}
            active={attendDay === d.id}
            onClick={() => onSelectDay(attendDay === d.id ? null : d.id)}
          />
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <TotalCard icon={Users} en="Confirmed participants" ar="المشاركون المؤكدون" value={p.total} loading={loading} testid="attendance-participants-total">
          Unique approved applicants, counted once (companions not counted).
          {" "}Includes {p.checked_in_included ?? "—"} checked-in.
          {" "}Day not specified: <span data-testid="attendance-day-not-specified">{p.day_not_specified ?? "—"}</span> (included in total, not in any day).
          {" "}Day cards can add up to more than this total because multi-day passes count on each day.
        </TotalCard>
        <TotalCard icon={Car} en="Total vehicles" ar="إجمالي المركبات" value={v.total} loading={loading} testid="attendance-vehicles-total">
          <span className="block">Outdoor 3-day pass · <Ar>تصريح الساحة الخارجية</Ar>: <b className="text-white" data-testid="attendance-vehicles-outdoor">{v.outdoor_three_day ?? "—"}</b></span>
          <span className="block">Indoor display, 3 days · <Ar>العرض الداخلي</Ar>: <b className="text-white" data-testid="attendance-vehicles-indoor">{v.indoor_three_day ?? "—"}</b></span>
          <span className="block">Day-specific (Day 1 / 2 / 3, Day 1 &amp; 3): <b className="text-white" data-testid="attendance-vehicles-day-specific">{v.day_specific ?? "—"}</b></span>
          <span className="block">Location not specified: <b className="text-white" data-testid="attendance-vehicles-not-specified">{v.location_not_specified ?? "—"}</b> (included in total)</span>
          <span className="block">Vehicle make/model not specified: <b className="text-white" data-testid="attendance-vehicles-details-missing">{v.details_missing ?? "—"}</b> (already included above)</span>
        </TotalCard>
        <TotalCard icon={Ticket} en="Expected visitors" ar="الزوار المتوقعون" value={vis.expected_headcount} loading={loading} testid="attendance-visitors-headcount">
          Visitor tickets only — separate from car participants above.
          {" "}<Ar>تذاكر الزوار فقط، منفصلة عن المشاركين بالسيارات.</Ar>
          <span className="grid grid-cols-3 gap-2 my-2" data-testid="attendance-visitors-by-day">
            {VISITOR_DAYS.map((d) => (
              <span key={d.id} className="border border-white/10 px-2 py-1.5">
                <span className="block text-[10px] uppercase tracking-wider text-white/50 whitespace-nowrap">{d.en}</span>
                <span className="block text-[10px] text-white/50 whitespace-nowrap"><Ar>{d.ar}</Ar></span>
                <b className="text-white text-base" data-testid={`attendance-visitors-day-${d.id}`}>{vis.by_day?.[d.id] ?? "—"}</b>
              </span>
            ))}
          </span>
          <span className="block">All three days (already inside each day above, not extra) · <Ar>الأيام الثلاثة (مشمولة في كل يوم أعلاه وليست إضافية)</Ar>: <b className="text-white" data-testid="attendance-visitors-all-three">{vis.all_three_days ?? "—"}</b></span>
          <span className="block">No day selected (in total, in no day) · <Ar>لم يُحدَّد يوم (ضمن الإجمالي، خارج الأيام)</Ar>: <b className="text-white" data-testid="attendance-visitors-no-days">{vis.no_days_selected ?? "—"}</b></span>
          <span className="block mt-1">Sum of people per ticket (registrant + family): <b className="text-white" data-testid="attendance-visitors-registrations">{vis.registrations_counted ?? "—"}</b> tickets counted.</span>
          <span className="block">Repeat email + mobile skipped: <b className="text-white" data-testid="attendance-visitors-duplicates">{vis.duplicates_skipped ?? "—"}</b></span>
          <span className="block">Invalid people count excluded: <b className="text-white" data-testid="attendance-visitors-invalid">{vis.invalid_quantity_excluded ?? "—"}</b></span>
          <span className="block mt-1 text-white/40" data-testid="attendance-visitors-cutoff-note">
            Visitor tickets have no edition tag; edition is set by registration date. Winter 2026 = registered on/after {summary?.edition_cutoff_utc ? `${summary.edition_cutoff_utc.slice(0, 16).replace("T", " ")} UTC` : "the Winter cutoff"} (20 Aug 2026, 00:00 Riyadh).
          </span>
        </TotalCard>
      </div>
      {attendDay && (
        <div className="mt-3 flex flex-wrap items-center gap-3 px-3 py-2 border border-[#F5C518]/50 bg-[#F5C518]/10 text-[#F5C518] text-xs" data-testid="attendance-day-filter-banner">
          <span>
            List filtered: approved participants attending {DAYS.find((d) => d.id === attendDay)?.en}
            {" "}({Object.keys(days[attendDay]?.by_assignment || {}).map((k) => ASSIGNMENT_LABELS[k]).join(", ")}).
          </span>
          <button type="button" onClick={() => onSelectDay(null)} data-testid="attendance-day-filter-clear" className="ms-auto inline-flex items-center gap-1 px-2 py-1 border border-[#F5C518]/60 hover:bg-[#F5C518]/20">
            <X className="w-3 h-3" /> Clear
          </button>
        </div>
      )}
    </section>
  );
};
